import React, { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useAuth } from './AuthContext';
import {
  comparisonSelectionReady,
  flushResearchComparisonFeedback,
  runResearchComparison,
  saveResearchComparisonFeedback,
} from '../services/localResearchComparison';
import type {
  ComparisonModelSelections,
  ComparisonPreference,
  ResearchComparisonCapabilities,
  ResearchComparisonEvent,
  ResearchComparisonFeedback,
  ResearchComparisonWorkspace,
  ResearchVariant,
  SavedResearchComparisonRun,
  VariantEvaluation,
  VariantRunState,
} from '../types/localResearchComparison';
import {
  applyComparisonEvent,
  createComparisonWorkspace,
  emptyComparisonEvaluation,
  emptyComparisonRunState,
  restoreComparisonWorkspace,
} from '../services/comparisonWorkspaceState';

const MAX_ACTIVE_COMPARISONS = 5;

const clientId = (): string => uuidv4();

interface ComparisonContextValue {
  workspaces: ResearchComparisonWorkspace[];
  activeWorkspace: ResearchComparisonWorkspace;
  activeCount: number;
  historyRevision: number;
  createDraft: () => string;
  selectWorkspace: (id: string) => void;
  updateDraft: (patch: Partial<Pick<ResearchComparisonWorkspace, 'question' | 'selection' | 'models'>>) => void;
  startActive: (capabilities: ResearchComparisonCapabilities) => Promise<boolean>;
  cancelWorkspace: (id: string) => void;
  openSavedRun: (saved: SavedResearchComparisonRun) => void;
  updateEvaluation: (variant: ResearchVariant, evaluation: VariantEvaluation) => void;
  updatePreference: (preference: ComparisonPreference) => void;
  persistActiveFeedback: () => Promise<boolean>;
}

const ResearchComparisonContext = createContext<ComparisonContextValue | null>(null);

export const ResearchComparisonProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const initial = useMemo(() => createComparisonWorkspace(clientId()), []);
  const [workspaces, setWorkspaces] = useState<Record<string, ResearchComparisonWorkspace>>({ [initial.clientId]: initial });
  const [activeWorkspaceId, setActiveWorkspaceId] = useState(initial.clientId);
  const [historyRevision, setHistoryRevision] = useState(0);
  const workspacesRef = useRef(workspaces);
  const controllersRef = useRef(new Map<string, AbortController>());
  const feedbackGenerationRef = useRef(new Map<string, number>());
  const feedbackTimersRef = useRef(new Map<string, number>());
  const identityRef = useRef<number | null>(null);
  const { user } = useAuth();

  const replaceWorkspaces = useCallback((next: Record<string, ResearchComparisonWorkspace>) => {
    workspacesRef.current = next;
    setWorkspaces(next);
  }, []);

  const updateWorkspace = useCallback((id: string, update: (workspace: ResearchComparisonWorkspace) => ResearchComparisonWorkspace) => {
    const current = workspacesRef.current[id];
    if (!current) return;
    replaceWorkspaces({ ...workspacesRef.current, [id]: update(current) });
  }, [replaceWorkspaces]);

  const clearAll = useCallback(() => {
    controllersRef.current.forEach(controller => controller.abort());
    controllersRef.current.clear();
    feedbackTimersRef.current.forEach(timer => window.clearTimeout(timer));
    feedbackTimersRef.current.clear();
    feedbackGenerationRef.current.clear();
    const fresh = createComparisonWorkspace(clientId());
    replaceWorkspaces({ [fresh.clientId]: fresh });
    setActiveWorkspaceId(fresh.clientId);
  }, [replaceWorkspaces]);

  useEffect(() => {
    const nextIdentity = user?.id ?? null;
    if (identityRef.current !== null && identityRef.current !== nextIdentity) clearAll();
    identityRef.current = nextIdentity;
  }, [clearAll, user?.id]);

  useEffect(() => () => {
    Object.values(workspacesRef.current).forEach(workspace => {
      if (workspace.runId && workspace.runStorageState === 'confirmed' && (workspace.feedbackSaveState === 'dirty' || workspace.feedbackSaveState === 'error')) {
        flushResearchComparisonFeedback(workspace.runId, { evaluations: workspace.evaluations, preference: workspace.preference });
      }
    });
    controllersRef.current.forEach(controller => controller.abort());
    feedbackTimersRef.current.forEach(timer => window.clearTimeout(timer));
  }, []);

  useEffect(() => {
    const hasUnsaved = Object.values(workspaces).some(workspace => workspace.feedbackSaveState === 'dirty' || workspace.feedbackSaveState === 'saving' || workspace.feedbackSaveState === 'error');
    if (!hasUnsaved) return;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [workspaces]);

  const createDraft = useCallback(() => {
    const fresh = createComparisonWorkspace(clientId());
    replaceWorkspaces({ ...workspacesRef.current, [fresh.clientId]: fresh });
    setActiveWorkspaceId(fresh.clientId);
    return fresh.clientId;
  }, [replaceWorkspaces]);

  const selectWorkspace = useCallback((id: string) => {
    if (workspacesRef.current[id]) setActiveWorkspaceId(id);
  }, []);

  const updateDraft = useCallback((patch: Partial<Pick<ResearchComparisonWorkspace, 'question' | 'selection' | 'models'>>) => {
    updateWorkspace(activeWorkspaceId, workspace => workspace.isRunning ? workspace : { ...workspace, ...patch });
  }, [activeWorkspaceId, updateWorkspace]);

  const applyEvent = useCallback((id: string, event: ResearchComparisonEvent) => {
    if (event.type === 'run.done') setHistoryRevision(value => value + 1);
    updateWorkspace(id, workspace => applyComparisonEvent(workspace, event));
  }, [updateWorkspace]);

  const startActive = useCallback(async (capabilities: ResearchComparisonCapabilities): Promise<boolean> => {
    const id = activeWorkspaceId;
    const workspace = workspacesRef.current[id];
    if (!workspace || workspace.isRunning || controllersRef.current.size >= MAX_ACTIVE_COMPARISONS) return false;
    const variants: ResearchVariant[] = workspace.selection === 'both' ? ['A', 'B'] : [workspace.selection];
    if (workspace.question.trim().length < 10 || !variants.every(variant => comparisonSelectionReady(variant, workspace.models[variant], capabilities))) return false;
    // Nur die Wahl der Seiten, die auch laufen: eine Wahl für eine stille
    // Seite weist der Server ab.
    const models: ComparisonModelSelections = Object.fromEntries(
      variants.flatMap(variant => (workspace.models[variant] ? [[variant, workspace.models[variant]]] : [])),
    );
    const controller = new AbortController();
    controllersRef.current.set(id, controller);
    feedbackGenerationRef.current.set(id, 0);
    const initialEvaluations = { A: emptyComparisonEvaluation(), B: emptyComparisonEvaluation() };
    updateWorkspace(id, current => ({
      ...current,
      submittedQuestion: current.question,
      runId: null,
      startedAt: new Date().toISOString(),
      runError: null,
      runStorageState: 'pending',
      isRunning: true,
      executedVariants: variants,
      executedModels: models,
      preference: null,
      evaluations: initialEvaluations,
      feedbackSaveState: 'idle',
      feedbackSavedAt: null,
      feedbackSaveError: null,
      states: {
        A: variants.includes('A') ? { ...emptyComparisonRunState(), status: 'running', statusMessage: 'Start wird vorbereitet …' } : emptyComparisonRunState(),
        B: variants.includes('B')
          ? capabilities.executionMode === 'sequential' && variants.includes('A')
            ? { ...emptyComparisonRunState(), status: 'queued', statusMessage: 'Wartet auf Variante A …' }
            : { ...emptyComparisonRunState(), status: 'running', statusMessage: 'Start wird vorbereitet …' }
          : emptyComparisonRunState(),
      },
    }));

    try {
      let completed = false;
      await runResearchComparison(
        {
          comparisonSessionId: id,
          userQuery: workspace.question,
          variants,
          ...(Object.keys(models).length > 0 ? { models } : {}),
        },
        event => {
          if (event.type === 'run.done' || event.type === 'run.error') completed = true;
          applyEvent(id, event);
        },
        controller.signal,
      );
      if (!completed) throw new Error('Der Vergleichsdatenstrom endete ohne Abschlussmeldung.');
      return true;
    } catch (error) {
      updateWorkspace(id, current => {
        const cancelled = controller.signal.aborted;
        const message = error instanceof Error ? error.message : 'Der Vergleich ist fehlgeschlagen.';
        const states = Object.fromEntries(Object.entries(current.states).map(([variant, state]) => [variant,
          state.status === 'running' || state.status === 'queued'
            ? cancelled
              ? { ...state, status: 'cancelled', statusMessage: 'Vom Benutzer abgebrochen' }
              : { ...state, status: 'error', error: { code: 'CLIENT_ERROR', message } }
            : state,
        ])) as Record<ResearchVariant, VariantRunState>;
        return { ...current, states, isRunning: false, ...(!cancelled ? { runError: message } : {}) };
      });
      return false;
    } finally {
      if (controllersRef.current.get(id) === controller) controllersRef.current.delete(id);
      updateWorkspace(id, current => ({ ...current, isRunning: false }));
    }
  }, [activeWorkspaceId, applyEvent, updateWorkspace]);

  const cancelWorkspace = useCallback((id: string) => controllersRef.current.get(id)?.abort(), []);

  const openSavedRun = useCallback((saved: SavedResearchComparisonRun) => {
    const existing = Object.values(workspacesRef.current).find(workspace => workspace.runId === saved.runId);
    if (existing) {
      setActiveWorkspaceId(existing.clientId);
      return;
    }
    const restored = restoreComparisonWorkspace(saved);
    replaceWorkspaces({ ...workspacesRef.current, [restored.clientId]: restored });
    setActiveWorkspaceId(restored.clientId);
  }, [replaceWorkspaces]);

  const markFeedback = useCallback((id: string, feedback: ResearchComparisonFeedback) => {
    feedbackGenerationRef.current.set(id, (feedbackGenerationRef.current.get(id) ?? 0) + 1);
    updateWorkspace(id, workspace => ({ ...workspace, ...feedback, feedbackSaveState: 'dirty', feedbackSaveError: null }));
  }, [updateWorkspace]);

  const updateEvaluation = useCallback((variant: ResearchVariant, evaluation: VariantEvaluation) => {
    const workspace = workspacesRef.current[activeWorkspaceId];
    if (workspace) markFeedback(activeWorkspaceId, { evaluations: { ...workspace.evaluations, [variant]: evaluation }, preference: workspace.preference });
  }, [activeWorkspaceId, markFeedback]);

  const updatePreference = useCallback((preference: ComparisonPreference) => {
    const workspace = workspacesRef.current[activeWorkspaceId];
    if (workspace) markFeedback(activeWorkspaceId, { evaluations: workspace.evaluations, preference });
  }, [activeWorkspaceId, markFeedback]);

  const persistFeedback = useCallback(async (id: string): Promise<boolean> => {
    const workspace = workspacesRef.current[id];
    if (!workspace?.runId || workspace.runStorageState !== 'confirmed') return false;
    const generation = feedbackGenerationRef.current.get(id) ?? 0;
    const feedback = { evaluations: workspace.evaluations, preference: workspace.preference };
    updateWorkspace(id, current => ({ ...current, feedbackSaveState: 'saving', feedbackSaveError: null }));
    try {
      const saved = await saveResearchComparisonFeedback(workspace.runId, feedback);
      if ((feedbackGenerationRef.current.get(id) ?? 0) !== generation) {
        updateWorkspace(id, current => ({ ...current, feedbackSaveState: 'dirty' }));
        return false;
      }
      updateWorkspace(id, current => ({ ...current, feedbackSaveState: 'saved', feedbackSavedAt: saved.feedbackUpdatedAt }));
      setHistoryRevision(value => value + 1);
      return true;
    } catch (error) {
      updateWorkspace(id, current => ({ ...current, feedbackSaveState: 'error', feedbackSaveError: error instanceof Error ? error.message : 'Die Bewertung konnte nicht gespeichert werden.' }));
      return false;
    }
  }, [updateWorkspace]);

  useEffect(() => {
    Object.values(workspaces).forEach(workspace => {
      const existing = feedbackTimersRef.current.get(workspace.clientId);
      const shouldSchedule = workspace.feedbackSaveState === 'dirty' && !workspace.isRunning && workspace.runStorageState === 'confirmed';
      if (existing !== undefined && !shouldSchedule) {
        window.clearTimeout(existing);
        feedbackTimersRef.current.delete(workspace.clientId);
      }
      if (shouldSchedule && existing === undefined) {
        const timer = window.setTimeout(() => {
          feedbackTimersRef.current.delete(workspace.clientId);
          void persistFeedback(workspace.clientId);
        }, 700);
        feedbackTimersRef.current.set(workspace.clientId, timer);
      }
    });
  }, [persistFeedback, workspaces]);

  const activeWorkspace = workspaces[activeWorkspaceId] ?? Object.values(workspaces)[0];
  const value = useMemo<ComparisonContextValue>(() => ({
    workspaces: Object.values(workspaces).sort((a, b) => {
      if (a.clientId === activeWorkspaceId) return -1;
      if (b.clientId === activeWorkspaceId) return 1;
      return (b.startedAt ?? '').localeCompare(a.startedAt ?? '');
    }),
    activeWorkspace,
    activeCount: controllersRef.current.size,
    historyRevision,
    createDraft,
    selectWorkspace,
    updateDraft,
    startActive,
    cancelWorkspace,
    openSavedRun,
    updateEvaluation,
    updatePreference,
    persistActiveFeedback: () => persistFeedback(activeWorkspaceId),
  }), [activeWorkspace, activeWorkspaceId, cancelWorkspace, createDraft, historyRevision, openSavedRun, persistFeedback, selectWorkspace, startActive, updateDraft, updateEvaluation, updatePreference, workspaces]);

  return <ResearchComparisonContext.Provider value={value}>{children}</ResearchComparisonContext.Provider>;
};

export const useResearchComparison = (): ComparisonContextValue => {
  const value = useContext(ResearchComparisonContext);
  if (!value) throw new Error('useResearchComparison must be used within ResearchComparisonProvider');
  return value;
};

export { MAX_ACTIVE_COMPARISONS };
